import React, { createContext, useContext, useEffect, useMemo } from 'react';
import useChangePlan from 'hooks/useChangePlan';
import { PLAN_BILLING_REDIRECT_PATH } from 'routes';

type FinaliseChargeState = { finalising: boolean };

const FinaliseChargeContext = createContext<FinaliseChargeState>({
  finalising: false,
});

/**
 * Confirms a Shopify approval the merchant has just returned from, exactly once.
 *
 * Pay as you sell needs two approvals — the one-time access fee, then the usage
 * subscription — so confirming the first usually answers with a redirect
 * straight to the second.
 *
 * This is a provider rather than a plain hook because confirmation must have a
 * single owner. Two components each running the mutation would fire two
 * `/shop/me/changePlan` requests for the same `charge_id`, with independent
 * loading state: they can race, and both can be handed a confirmation URL to
 * redirect to.
 */
export const FinaliseChargeProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  // An empty `?charge_id=` is not a charge. Normalising here keeps the request
  // and the `finalising` flag agreeing — treating '' as present but skipping the
  // request would leave the UI waiting on something that never runs.
  const chargeId =
    new URLSearchParams(window.location.search).get('charge_id') || null;

  const { mutate: changePlan, isPending, isIdle } = useChangePlan();

  useEffect(() => {
    if (chargeId)
      changePlan({
        chargeId,
        redirectPath: PLAN_BILLING_REDIRECT_PATH,
        planType: null,
      });
  }, [changePlan, chargeId]);

  // Idle counts as finalising: on first render the effect has not fired yet, and
  // treating that as finished would flash the page before the request starts.
  const finalising = chargeId !== null && (isIdle || isPending);

  // This provider wraps the routed content, so an unmemoised value would make
  // every consumer re-render whenever the mutation state changes.
  const value = useMemo(() => ({ finalising }), [finalising]);

  return (
    <FinaliseChargeContext.Provider value={value}>
      {children}
    </FinaliseChargeContext.Provider>
  );
};

/**
 * Whether a returning approval is still being confirmed. Observes the provider —
 * it never starts a request of its own.
 */
export default function useFinaliseCharge(): FinaliseChargeState {
  return useContext(FinaliseChargeContext);
}
